import {
  ArrowDownLeft,
  ArrowUpRight,
  History,
  LogOut,
  ShoppingBag,
  UserRound,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  loadAccountActivity,
  loginAccount,
  logoutAccount,
  registerAccount,
} from "../lib/api";
import type { AccountActivity, PublicConfig } from "../types";

type Props = {
  config: PublicConfig;
  open: boolean;
  onClose: () => void;
  onChange: (change: Pick<PublicConfig, "profile" | "credits">) => void;
  onBuy: () => void;
};

const activityCopy: Record<AccountActivity["kind"], string> = {
  generation: "Vocale creato",
  purchase: "Crediti acquistati",
  refund: "Credito restituito",
  admin_grant: "Credito assistenza",
};

const formatDay = (value: string) =>
  new Intl.DateTimeFormat("it-IT", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));

export function AccountPanel({
  config,
  open,
  onClose,
  onChange,
  onBuy,
}: Props) {
  const [mode, setMode] = useState<"register" | "login">("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [activity, setActivity] = useState<AccountActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (!config.profile) {
      setActivity([]);
      return;
    }
    loadAccountActivity()
      .then(({ activity: recent }) => setActivity(recent))
      .catch(() => setActivity([]));
  }, [config.profile, open]);

  if (!open) return null;

  const authenticate = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result =
        mode === "register"
          ? await registerAccount({ email, password })
          : await loginAccount({ email, password });
      onChange(result);
      setPassword("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Accesso non riuscito.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setError("");
    setLoading(true);
    try {
      const result = await logoutAccount();
      onChange(result);
      setMode("login");
      setEmail("");
      setActivity([]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Uscita non riuscita.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="account-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="modal-close" type="button" onClick={onClose}>
          <X aria-hidden="true" /> <span className="sr-only">Chiudi</span>
        </button>

        {config.profile ? (
          <>
            <div className="account-heading">
              <span className="account-avatar" aria-hidden="true"><UserRound /></span>
              <div>
                <small>Il tuo profilo</small>
                <h2 id="account-title">{config.profile.email}</h2>
              </div>
            </div>
            <div className="account-balance">
              <span>Disponibili</span>
              <strong>{config.credits} VAFFA</strong>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onBuy();
                }}
              >
                <ShoppingBag /> Acquista
              </button>
            </div>
            <div className="activity-heading">
              <History aria-hidden="true" />
              <h3>Ultimi 30 giorni</h3>
            </div>
            {activity.length ? (
              <ul className="activity-list">
                {activity.map((item) => (
                  <li key={item.id}>
                    <span className={`activity-icon activity-icon--${item.kind}`}>
                      {item.creditsDelta < 0 ? <ArrowUpRight /> : <ArrowDownLeft />}
                    </span>
                    <span>
                      <strong>{activityCopy[item.kind]}</strong>
                      <small>{formatDay(item.createdAt)}</small>
                    </span>
                    <b>{item.creditsDelta > 0 ? "+" : ""}{item.creditsDelta}</b>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="activity-empty">Qui compariranno creazioni e acquisti. Mai il testo delle battute.</p>
            )}
            <button className="account-logout" type="button" onClick={logout} disabled={loading}>
              <LogOut /> Esci dal profilo
            </button>
          </>
        ) : (
          <>
            <div className="account-heading account-heading--guest">
              <span className="account-avatar" aria-hidden="true"><UserRound /></span>
              <div>
                <small>Crediti al sicuro</small>
                <h2 id="account-title">
                  {mode === "register" ? "Crea il tuo profilo" : "Bentornato"}
                </h2>
              </div>
            </div>
            <p className="account-intro">
              Ritrova i tuoi VAFFA anche cambiando telefono. Bastano email e password.
            </p>
            <form className="account-form" onSubmit={authenticate}>
              <label htmlFor="account-email">Email</label>
              <input
                id="account-email"
                type="email"
                value={email}
                autoComplete="email"
                required
                onChange={(event) => setEmail(event.target.value)}
              />
              <label htmlFor="account-password">Password</label>
              <input
                id="account-password"
                type="password"
                value={password}
                minLength={8}
                maxLength={72}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                required
                onChange={(event) => setPassword(event.target.value)}
              />
              <button className="account-submit" type="submit" disabled={loading}>
                {loading
                  ? "Un istante…"
                  : mode === "register"
                    ? "Crea profilo"
                    : "Accedi"}
              </button>
            </form>
            {error ? <p className="form-error account-error">{error}</p> : null}
            <button
              className="account-switch"
              type="button"
              onClick={() => {
                setMode((current) => current === "register" ? "login" : "register");
                setError("");
              }}
            >
              {mode === "register" ? "Hai già un profilo? Accedi" : "Non hai un profilo? Crealo"}
            </button>
            <p className="account-privacy">Password protetta. Le battute non vengono salvate.</p>
          </>
        )}
        {config.profile && error ? <p className="form-error account-error">{error}</p> : null}
      </section>
    </div>
  );
}
